import React, { useState, forwardRef } from 'react';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import OutlinedInput from '@mui/material/OutlinedInput';
import Input from '@mui/material/Input';
import EDFormHelperText from '../FormHelperText/EDFormHelperText';
import EDTooltip from '../Tooltip/EDTooltip';
import EDIconButton from '../Buttons/IconButton/EDIconButton';
import useInputVariant from '../../Hooks/useInputVariant';
import { ChangedMarkerLine } from '../../Components/ChangedMarker/ChangedMarker';
import { useInputGroup } from '../../Hooks/useInputGroup';
import { createPortal } from 'react-dom';
import AvenyTheme, {
  AvenyThemeSpacing,
  lightThemeTextPrimaryColor,
} from '../ThemeProvider/AvenyTheme';
import {
  useIsBreakpointOrSmaller,
  useIsTouchDevice,
} from '../../Components/BreakpointContext/BreakpointContextUtils';
import {
  getAttributeWidth,
  getRelativeAttributeWidth,
} from '../../Components/Attributes/Utils/AttributeValueAutoSizeUtils';
import { IEditComponentProps } from '../../Components/Attributes/EditableAttribute/EditComponentTypes';
import EDBox from '../EDBox/EDBox';
import { MASKED_VALUE_TEXT } from '../../Features/Delsystem/_Common/Hooks/useAttributeValues';
import { useFullscreenView } from '../../Hooks/useFullscreenView';

interface IProps extends IEditComponentProps<any> {
  allMargins?: 'dense' | 'normal' | 'none';
  largeStartAdornmentPadding?: boolean;
  showPasswordToggle?: boolean;
  inputBaseClassName?: string;
  /** Anpassa padding runt inputbase och input för att hantera att input ska visas tillsammans med chips på en eller flera rader */
  multiselectPadding?: boolean;
  /** props.endAdorment är absolutpositionerat (t.ex. från MuiAutocomplete) */
  absolutePostionedEndAdornment?: boolean;
  ref?: React.Ref<HTMLDivElement>;
}
interface ITextEditEndAdornmentProps {
  hasEndAdornment?: boolean;
  hideOutline?: boolean;
  absolutePostionedEndAdornment?: boolean;
}
export const TextEditEndAdornment: React.FC<
  React.PropsWithChildren<ITextEditEndAdornmentProps>
> = props =>
  props.absolutePostionedEndAdornment ? (
    <EDBox
      sx={{
        position: 'absolute',
        right: props.hasEndAdornment ? '32px' : props.hideOutline ? 0 : '4px',
        top: '50%',
        transform: 'translate(0, -50%)',
      }}
    >
      {props.children}
    </EDBox>
  ) : (
    <>{props.children}</>
  );

export const EDTextEdit: React.FC<React.PropsWithChildren<IProps>> = forwardRef(
  (props, ref) => {
    const smallScreen = useIsBreakpointOrSmaller('sm');
    const isTouchDevice = useIsTouchDevice();
    const editInFullScreenView = useFullscreenView();
    const inputVariant = useInputVariant();
    const [showPassword, setShowPassword] = useState(false);

    const disabled = props.masked ? true : props.disabled;
    const value = props.masked ? MASKED_VALUE_TEXT : props.value;

    const {
      onFocus: inputGroupOnFocus,
      ref: inputGroupRef,
      active,
      navigateNext,
    } = useInputGroup(
      props.onFocus,
      disabled || props.disableInputGroup,
    );

    const [isFocused, setIsFocused] = useState<boolean>(false);

    const onFocus = (event: React.FocusEvent<HTMLElement, Element>) => {
      setIsFocused(true);
      inputGroupOnFocus?.(event);
    };

    const onBlur = (event: React.FocusEvent<HTMLInputElement, Element>) => {
      setIsFocused(false);
      props.onBlur?.(event);
    };

    const outlined = inputVariant === 'outlined' && !props.simpleLine;

    // When in fullscreen edit view, the text edit should autoFocus when active,
    //  otherwise it should follow the autoFocus prop. 
    const autoFocus =
      (props.autoFocus && !editInFullScreenView) ||
      (props.disableInputGroup ? props.inputGroupActive : active);

    const autoCompleteRef = props.disableInputGroup
      ? props.inputGroupRef ?? null
      : inputGroupRef;

    const inputGroupActive = props.disableInputGroup
      ? Boolean(props.inputGroupActive)
      : active;

    const inputGroupGoNext = props.disableInputGroup
      ? props.inputGroupGoNext ?? (() => {})
      : navigateNext;

    const fullWidth: boolean =
      props.fullWidth || props.size === 'ownblock' || props.size === undefined;
    const inputBaseRef = props.inputBaseRef ?? (() => {});

    const onChange = (
      event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
    ) => {
      if (props.eventOnChange) {
        props.eventOnChange(event);
      } else if (props.onChange) {
        props.onChange(event.target.value);
      }
    };

    const onClick = (event: React.MouseEvent<HTMLInputElement>) => {
      (event.currentTarget.firstElementChild as any)?.focus?.();

      if (props.onClick) {
        props.onClick(event);
      }
    };

    const reset = (event: React.MouseEvent) => {
      if (props.onReset) {
        props.onReset(event);
      } else if (props.onChange) {
        props.onChange('');
      }
    };
    const handleTogglePasswordVisibility = (event: React.MouseEvent) => {
      event.stopPropagation();
      setShowPassword(prev => !prev);
    };
    const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (props.onKeyDown) {
        props.onKeyDown(event);
      }
      if (inputGroupActive && (event.keyCode === 9 || event.keyCode === 13)) {
        event.preventDefault();
        if (props.onBlur) {
          props.onBlur(event as any as React.FocusEvent<HTMLInputElement>);
        }
        inputGroupGoNext();
      }
    };

    const shrink: boolean =
      props.staticLabel || isFocused || (value != null && value !== '');

    const width =
      props.width ??
      (fullWidth
        ? '100%'
        : smallScreen && props.smallScreenSize
        ? props.smallScreenSize
        : props.relativeSize
        ? getRelativeAttributeWidth(props.size)
        : getAttributeWidth(props.size));
    const totalEndAdornmentsInProp = props.endAdornment
      ? props.totalEndAdornmentsInProp ?? 1
      : 0;
    const hoverEndAdornmentsInProp = props.hoverEndAdornmentsInProp ?? 0;
    const fixedEndAdornments =
      totalEndAdornmentsInProp - hoverEndAdornmentsInProp;
    const hoverEndAdornments =
      hoverEndAdornmentsInProp + (props.hideClearIcon ? 0 : 1);
    const fixedEndAdornmentPadding = AvenyThemeSpacing * 3 * fixedEndAdornments;
    const hoverEndAdornmentPadding = AvenyThemeSpacing * 3 * hoverEndAdornments;

    const HelperText = (props.helperText ||
      (props.required && !value) ||
      props.characterCount ||
      props.usesHelperText) && (
      <>
        <EDFormHelperText
          margin="dense"
          error={props.error}
          warning={props.warning}
          lineMarker={props.hideOutline}
          data-testid={`${props.testId}-helperText`}
        >
          {props.helperText
            ? props.helperText
            : !value && props.required
            ? props.requiredMessage ?? 'Värde krävs'
            : ' '}
        </EDFormHelperText>
        {props.characterCount !== undefined && (
          <EDFormHelperText error={false}>
            {props.characterCount}
          </EDFormHelperText>
        )}
      </>
    );

    const hasEndAdornment: boolean =
      (props.value && !props.hideClearIcon && !disabled) ||
      props.endAdornment ||
      (props.showPasswordToggle && props.type === 'password');
    const endAdorment = hasEndAdornment ? (
      <>
        <TextEditEndAdornment
          hideOutline={props.hideOutline}
          hasEndAdornment={!!props.endAdornment}
          absolutePostionedEndAdornment={props.absolutePostionedEndAdornment}
        >
          {props.value && !props.hideClearIcon && !disabled && (
            <EDBox
              sx={{
                display: 'none',
                '.MuiInputBase-root:hover &': { display: 'block' },
                '.MuiInputBase-root.Mui-focused &': { display: 'block' },
              }}
            >
              <EDIconButton
                icon="close"
                padding={props.adornmentPadding ?? 'dense'}
                margin="none"
                tooltip={props.tooltipRensa || 'Rensa'}
                iconColor="action"
                iconSize={'medium'}
                onClick={reset}
                disableTabbing
              />
            </EDBox>
          )}
          {props.showPasswordToggle && props.type === 'password' && (
            <EDBox
              sx={{
                display: 'block',
                position: 'relative',
                marginRight: '4px',
              }}
            >
              <EDIconButton
                icon={showPassword ? 'visibility_off' : 'visibility'}
                padding={props.adornmentPadding ?? 'dense'}
                margin="none"
                tooltip={showPassword ? 'Dölj lösenord' : 'Visa lösenord'}
                iconColor="action"
                iconSize="medium"
                onClick={handleTogglePasswordVisibility}
                disableTabbing
                disabled={disabled}
              />
            </EDBox>
          )}
        </TextEditEndAdornment>
        {props.endAdornment}
      </>
    ) : undefined;
    const InputComponent = props.simpleLine ? Input : OutlinedInput;
    const inputComponentPadding = props.multiselectPadding ? '3px' : 0;
    const textEdit = (
      <EDBox
        sx={[
          {
            boxSizing: 'border-box',
            display: 'inline-block',
            width,
            padding: props.padding,
            paddingX: props.paddingX,
            paddingY: props.paddingY,
            paddingLeft: props.paddingLeft,
            paddingRight: props.paddingRight,
            paddingTop: props.paddingTop,
            paddingBottom: props.paddingBottom,
            '& .MuiFormControl-root .MuiAutocomplete-endAdornment': {
              right: props.hideOutline ? 0 : 4,
            },
          },
        ]}
      >
        <FormControl
          fullWidth
          size="small"
          disabled={disabled}
          margin={props.allMargins ?? 'normal'}
          sx={[
            {
              boxSizing: 'border-box',
            },
          ]}
          variant={outlined ? 'outlined' : 'standard'}
          required={props.required}
          ref={ref}
        >
          {!props.hideLabel && (
            <InputLabel
              sx={[
                !!props.largeStartAdornmentPadding &&
                  !shrink && {
                    paddingLeft: '16px',
                    maxWidth: 'calc(100% - 64px)',
                  },
                {
                  '& .MuiInputLabel-asterisk ': {
                    color:
                      value === '' ? AvenyTheme.palette.error.main : undefined,
                  },
                  '&.MuiInputLabel-shrink': {
                    color: theme =>
                      props.error
                        ? theme.palette.error.main
                        : props.warning
                        ? theme.palette.warning.main
                        : undefined,
                  },
                },
              ]}
              required={props.required}
              disabled={false}
              shrink={shrink}
              htmlFor={props.id}
              disableAnimation={props.disableAnimation}
              hidden={props.hideLabel}
              data-testid={`${props.testId}-label`}
            >
              {props.label}
            </InputLabel>
          )}
          <EDBox className="EDBox-textedit" sx={{ position: 'relative' }}>
            {props.marker && (
              <ChangedMarkerLine
                shape={props.hideOutline ? 'datagridBorder' : 'outlineBorder'}
              />
            )}
            <EDTooltip
              title={props.marker ? 'Fältet har ändrats' : props.tooltip ?? ''}
              placement={'bottom-start'}
              id={props.id}
            >
              <InputComponent
                sx={[
                  {
                    color: props.grayTextColor
                      ? 'text.disabled'
                      : 'text.primary',
                    paddingLeft: '4px',
                    paddingRight: '4px',
                    paddingTop: inputComponentPadding,
                    paddingBottom: inputComponentPadding,
                    width: '100%',
                    flexWrap: 'wrap',
                    input: {
                      fontSize: isTouchDevice ? '1rem' : '0.865rem', //iOS zoomar automatiskt in inputfältet vid focus om font-size är mindre än 16px
                      overflow: 'visible',
                    },
                    '& input::placeholder': {
                      fontSize: isTouchDevice ? '1rem' : '0.865rem', //iOS zoomar automatiskt in inputfältet vid focus om font-size är mindre än 16px
                      overflow: 'visible',
                    },
                    backgroundColor:
                      (disabled || props.readOnly) && !props.hideOutline
                        ? 'action.disabledBackground'
                        : undefined,
                    // theme.palette.action.disabledBackground,
                    '.MuiFormControl-root.MuiFormControl-fullWidth &': {
                      paddingLeft: '4px',
                      paddingRight: '4px',
                      paddingTop: inputComponentPadding,
                      paddingBottom: inputComponentPadding,
                    },
                    '.MuiFormControl-root .EDBox-textedit & .MuiInputBase-input':
                      {
                        flex: '1 1 50px',
                      },
                  },
                  (!!disabled || !!props.readOnly) && {
                    color: 'text.primary', //samma färg som standard men vi sätter det här för att det inte ska ändras vid disabled
                    '&& .MuiInputBase-input': {
                      color: 'text.primary',
                      textFillColor: lightThemeTextPrimaryColor, //borde vara text.primary men temafärger hanteras inte av mui för fält som inte är standard
                    },
                  },
                  !props.simpleLine && {
                    '.MuiFormControl-root .EDBox-textedit && .MuiInputBase-input':
                      {
                        paddingLeft: 1,
                        paddingTop: props.multiselectPadding ? '5px' : 1,
                        paddingBottom: props.multiselectPadding ? '5px' : 1,
                      },
                  },
                  !!props.simpleLine && {
                    marginTop: 1,
                    '& .MuiInputBase-input': {
                      paddingLeft: 0,
                      paddingTop: '6px',
                      paddingBottom: '5px',
                    },
                  },
                  !!props.hideOutline && {
                    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                    '& .MuiInputBase-input': {
                      paddingRight: 0.5,
                    },
                    '.MuiFormControl-root.MuiFormControl-fullWidth &.MuiInputBase-adornedEnd':
                      {
                        paddingLeft: '4px',
                        paddingRight: '4px',
                        paddingTop: 0,
                        paddingBottom: 0,
                      },
                  },
                  !!props.absolutePostionedEndAdornment && {
                    '.MuiFormControl-root.MuiFormControl-fullWidth:hover &.MuiInputBase-adornedEnd':
                      {
                        paddingRight: `${
                          fixedEndAdornmentPadding + hoverEndAdornmentPadding
                        }px`,
                      },
                  },
                  !!props.absolutePostionedEndAdornment && {
                    '.MuiFormControl-root.MuiFormControl-fullWidth &.MuiInputBase-adornedEnd':
                      {
                        paddingRight: `${
                          fixedEndAdornmentPadding +
                          (isFocused ? hoverEndAdornmentPadding : 0)
                        }px`,
                      },
                  },
                  !!props.rightAlign && {
                    '& .MuiInputBase-input': { textAlign: 'right' },
                  },
                ]}
                className={props.inputBaseClassName}
                required={props.required}
                onClick={onClick}
                ref={inputBaseRef}
                inputRef={props.inputRef}
                autoFocus={autoFocus}
                name={props.id}
                type={
                  props.type === 'password'
                    ? showPassword
                      ? 'text'
                      : 'password'
                    : props.type ?? 'text'
                }
                value={value}
                id={props.id}
                autoComplete={props.autoComplete}
                inputProps={{
                  'data-testid': props.testId ?? props.id,
                  ...props.inputProps,
                }}
                onBlur={onBlur}
                onChange={onChange}
                maxRows={
                  props.multiline === true && props.maxRows !== undefined
                    ? props.maxRows
                    : undefined
                }
                minRows={
                  props.multiline === true && props.minRows !== undefined
                    ? props.minRows
                    : undefined
                }
                multiline={props.multiline}
                startAdornment={props.startAdornment}
                endAdornment={endAdorment}
                onFocus={onFocus}
                onKeyDown={onKeyDown}
                onKeyUp={props.onKeyUp}
                onMouseDown={props.onMouseDown}
                placeholder={shrink ? props.placeholder : undefined}
                spellCheck={false}
                notched={shrink}
                fullWidth={fullWidth}
                label={props.label}
                margin={
                  props.allMargins === 'normal' ? undefined : props.allMargins
                }
                readOnly={props.readOnly}
              />
            </EDTooltip>
          </EDBox>
          {HelperText}
        </FormControl>
      </EDBox>
    );
    const renderInPortal: boolean =
      inputGroupActive &&
      !!autoCompleteRef &&
      !props.disableInputGroup &&
      !props.fullscreenStillRenderTextEdit;
    return (
      <>
        {renderInPortal && !!autoCompleteRef
          ? createPortal(textEdit, autoCompleteRef)
          : textEdit}
      </>
    );
  }
);
EDTextEdit.displayName = 'EDTextEdit';
